import { useEffect, useState } from 'react';
import {
  isQuoteShareActive,
  publicQuoteUrl,
  quoteShareMessage,
  whatsAppShareUrl,
} from '../lib/quoteShareUrl';
import { supabase } from '../lib/supabase';

function qrImageUrl(url) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`;
}

export default function QuoteShareActions({ quote, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [published, setPublished] = useState(quote);

  useEffect(() => {
    setPublished(quote);
  }, [quote]);

  if (!published?.id || published.status === 'draft') return null;

  const active = isQuoteShareActive(published);
  const shareUrl = active ? publicQuoteUrl(published.share_token) : null;

  async function publishShare() {
    setBusy(true);
    setError('');
    setOk('');
    const { data, error: rpcError } = await supabase.rpc('publish_quotation_share', {
      p_quotation_id: published.id,
      p_expires_days: 90,
    });
    setBusy(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    const next = { ...published, ...data };
    setPublished(next);
    onRefresh?.(next);
    const url = publicQuoteUrl(data.share_token);
    try {
      await navigator.clipboard.writeText(url);
      setOk('Share link published and copied.');
    } catch {
      setOk('Share link published.');
    }
  }

  async function revokeShare() {
    setBusy(true);
    setError('');
    setOk('');
    const { data, error: rpcError } = await supabase.rpc('revoke_quotation_share', {
      p_quotation_id: published.id,
    });
    setBusy(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    const next = { ...published, ...data };
    setPublished(next);
    onRefresh?.(next);
    setShowQr(false);
    setOk('Share link revoked.');
  }

  async function copyLink() {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setOk('Link copied.');
  }

  return (
    <div className="quote-share-actions">
      {error ? <p className="form-error">{error}</p> : null}
      {ok ? <p className="form-ok">{ok}</p> : null}

      <div className="quote-share-row">
        <button type="button" className="ghost" disabled={busy} onClick={publishShare}>
          {shareUrl ? 'Refresh link' : 'Publish web link'}
        </button>
        {shareUrl ? (
          <>
            <button type="button" className="ghost" disabled={busy} onClick={copyLink}>
              Copy link
            </button>
            <button type="button" className="ghost" onClick={() => setShowQr((v) => !v)}>
              {showQr ? 'Hide QR' : 'Show QR'}
            </button>
            <a
              className="ghost"
              href={whatsAppShareUrl(quoteShareMessage(published, shareUrl))}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
            </a>
            <button type="button" className="ghost" disabled={busy} onClick={revokeShare}>
              Revoke
            </button>
          </>
        ) : null}
      </div>

      {showQr && shareUrl ? (
        <div className="quote-share-qr">
          <img src={qrImageUrl(shareUrl)} alt="Quotation QR" />
          <a href={shareUrl} target="_blank" rel="noreferrer">
            {shareUrl}
          </a>
        </div>
      ) : null}
    </div>
  );
}
